import { create } from 'zustand';
import type { AttendanceTodayDto, OfflineSyncResultDto } from '@fas/types';
import { api, ApiError } from './api';
import { useAuthStore } from './auth-store';

/*
 * Clock events recorded on this device without a connection (§57, Phase 2D, ADR-046). They
 * wait in IndexedDB, go to the server when a connection returns, and are validated there,
 * flagged and reviewed — the device never decides that attendance counted. The last known
 * offset between server and device time travels with each event, because the device clock
 * is not authoritative (§71).
 */

export interface QueuedClockEvent {
  /** Also the idempotency key: sending it twice records it once. */
  clientRequestId: string;
  userId: string;
  kind: 'CLOCK_IN' | 'CLOCK_OUT';
  sessionId: string;
  /** For display on the device: "NURS 101 A", "Daily attendance". */
  subject: string;
  sessionType: 'CLASS' | 'DAILY';
  actualModality?: string | null;
  workArrangement?: string | null;
  onlinePlatform?: string | null;
  onlineMeetingRef?: string | null;
  method: 'WEB_BROWSER' | 'MOBILE_GPS';
  location?: { latitude: number; longitude: number; accuracyMeters: number | null } | null;
  gpsError?: string | null;
  capturedAt: string;
  skewMs: number;
  device?: { publicKey: unknown; signature: string } | null;
  status: 'PENDING' | 'REJECTED';
  code?: string | null;
  message?: string | null;
}

/** The day's sessions as last seen online, for recording attendance after an offline start. */
export interface CachedToday {
  userId: string;
  userName: string;
  savedAt: string;
  data: AttendanceTodayDto;
}

interface Store {
  getAll(): Promise<QueuedClockEvent[]>;
  put(event: QueuedClockEvent): Promise<void>;
  remove(ids: string[]): Promise<void>;
  getToday(): Promise<CachedToday | null>;
  putToday(today: CachedToday): Promise<void>;
}

const DB_NAME = 'fas-offline';
const SKEW_KEY = 'fas.serverSkewMs';
const BATCH = 20;

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function indexedDbStore(): Store {
  const open = () => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore('events', { keyPath: 'clientRequestId' });
      req.result.createObjectStore('today');
    };
    return request(req);
  };
  const run = async <T>(name: 'events' | 'today', mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    try {
      return await request(fn(db.transaction(name, mode).objectStore(name)));
    } finally {
      db.close();
    }
  };
  return {
    getAll: () => run('events', 'readonly', (s) => s.getAll() as IDBRequest<QueuedClockEvent[]>),
    put: async (e) => void (await run('events', 'readwrite', (s) => s.put(e))),
    remove: async (ids) => {
      for (const id of ids) await run('events', 'readwrite', (s) => s.delete(id));
    },
    getToday: async () => ((await run('today', 'readonly', (s) => s.get('last'))) as CachedToday | undefined) ?? null,
    putToday: async (t) => void (await run('today', 'readwrite', (s) => s.put(t, 'last'))),
  };
}

/** Where IndexedDB is unavailable (tests, some private windows): kept for this visit only. */
function memoryStore(): Store {
  const events = new Map<string, QueuedClockEvent>();
  let today: CachedToday | null = null;
  return {
    getAll: async () => [...events.values()],
    put: async (e) => void events.set(e.clientRequestId, e),
    remove: async (ids) => ids.forEach((id) => events.delete(id)),
    getToday: async () => today,
    putToday: async (t) => void (today = t),
  };
}

let store: Store = typeof indexedDB === 'undefined' ? memoryStore() : indexedDbStore();

/** Counts for the banners; refreshed after every change. */
export const useOfflineQueue = create<{ events: QueuedClockEvent[] }>(() => ({ events: [] }));

async function refresh(): Promise<QueuedClockEvent[]> {
  const all = (await store.getAll().catch(() => [])).sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
  useOfflineQueue.setState({ events: all });
  return all;
}

// ── Server time ──────────────────────────────────────────────────────────────
/** Remembers the server's clock relative to this device's (called with each fresh server time). */
export function recordServerTime(serverTime: string, receivedAt = Date.now()): void {
  const skew = Date.parse(serverTime) - receivedAt;
  if (!Number.isFinite(skew)) return;
  try {
    localStorage.setItem(SKEW_KEY, String(Math.round(skew)));
  } catch {
    // Storage blocked: the offset defaults to zero.
  }
}

export function currentSkewMs(): number {
  try {
    const v = Number(localStorage.getItem(SKEW_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

// ── The day's sessions, for an offline start ──────────────────────────────────
export async function cacheToday(data: AttendanceTodayDto): Promise<void> {
  const me = useAuthStore.getState().me;
  if (!me) return;
  await store.putToday({ userId: me.user.id, userName: me.faculty?.fullName ?? me.user.displayName ?? me.user.email, savedAt: new Date().toISOString(), data }).catch(() => undefined);
}

/** The cached day, if it is still today on this device (in the institution's time zone). */
export async function cachedToday(): Promise<CachedToday | null> {
  const cached = await store.getToday().catch(() => null);
  if (!cached) return null;
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: cached.data.timeZone }).format(new Date(Date.now() + currentSkewMs()));
  return cached.data.date === today ? cached : null;
}

// ── Queue ──────────────────────────────────────────────────────────────────────
export async function queueClockEvent(event: Omit<QueuedClockEvent, 'status'>): Promise<void> {
  await store.put({ ...event, status: 'PENDING' });
  await refresh();
}

export async function loadOfflineQueue(): Promise<QueuedClockEvent[]> {
  return refresh();
}

export async function dismissOfflineEvent(clientRequestId: string): Promise<void> {
  await store.remove([clientRequestId]);
  await refresh();
}

/** The request body of one event: only what the server accepts, without empty fields. */
function payloadOf(e: QueuedClockEvent): Record<string, unknown> {
  const body: Record<string, unknown> = {
    kind: e.kind,
    clientRequestId: e.clientRequestId,
    sessionId: e.sessionId,
    capturedAt: e.capturedAt,
    skewMs: e.skewMs,
    method: e.method,
  };
  const optional = { location: e.location, gpsError: e.gpsError, device: e.device } as const;
  const clockIn = e.kind === 'CLOCK_IN' ? { sessionType: e.sessionType, actualModality: e.actualModality, workArrangement: e.workArrangement, onlinePlatform: e.onlinePlatform, onlineMeetingRef: e.onlineMeetingRef } : {};
  for (const [key, value] of Object.entries({ ...optional, ...clockIn })) if (value !== null && value !== undefined) body[key] = value;
  return body;
}

let syncing: Promise<{ accepted: number; rejected: number }> | null = null;

/**
 * Sends the signed-in user's pending events (oldest first). Accepted and already-recorded
 * events leave the device; refused ones stay with the reason until the user dismisses them.
 * Without a connection nothing changes and the events wait.
 */
export function syncOfflineEvents(): Promise<{ accepted: number; rejected: number }> {
  syncing ??= (async () => {
    const userId = useAuthStore.getState().me?.user.id;
    let accepted = 0;
    let rejected = 0;
    if (!userId || useAuthStore.getState().status !== 'authenticated') return { accepted, rejected };
    const pending = (await refresh()).filter((e) => e.userId === userId && e.status === 'PENDING');
    for (let i = 0; i < pending.length; i += BATCH) {
      const batch = pending.slice(i, i + BATCH);
      let res: OfflineSyncResultDto;
      try {
        res = await api.post<OfflineSyncResultDto>('/attendance/offline-events', { events: batch.map(payloadOf) });
      } catch (err) {
        if (err instanceof ApiError && err.code === 'NETWORK_ERROR') break;
        throw err;
      }
      const done: string[] = [];
      for (const r of res.results) {
        const e = batch.find((b) => b.clientRequestId === r.clientRequestId);
        if (!e) continue;
        if (r.outcome === 'REJECTED') {
          rejected += 1;
          await store.put({ ...e, status: 'REJECTED', code: r.code, message: r.message });
        } else {
          accepted += 1;
          done.push(e.clientRequestId);
        }
      }
      await store.remove(done);
    }
    await refresh();
    return { accepted, rejected };
  })().finally(() => {
    syncing = null;
  });
  return syncing;
}

/** Test hook: a fresh in-memory store. */
export function resetOfflineQueueForTests(): void {
  store = memoryStore();
  useOfflineQueue.setState({ events: [] });
}
